/**
 * InventorySystem -- Manages the party's shared inventory bag and equipment.
 *
 * Provides methods to add/remove items, equip/unequip to characters,
 * and recalculate derived stats when equipment changes.
 * Emits events for UI updates.
 *
 * @module InventorySystem
 */

import eventBus from '../core/EventBus.js';
import lootSystem from './LootSystem.js';

/** Maximum inventory bag size. */
const MAX_BAG_SIZE = 60;

/** Equipment slot display info. */
const SLOT_INFO = {
  head:    { name: 'Head',    icon: 'H' },
  chest:   { name: 'Chest',   icon: 'C' },
  legs:    { name: 'Legs',    icon: 'L' },
  boots:   { name: 'Boots',   icon: 'B' },
  gloves:  { name: 'Gloves',  icon: 'G' },
  weapon:  { name: 'Weapon',  icon: 'W' },
  offhand: { name: 'Offhand', icon: 'O' },
  ring1:   { name: 'Ring 1',  icon: 'R' },
  ring2:   { name: 'Ring 2',  icon: 'R' },
  amulet:  { name: 'Amulet',  icon: 'A' },
};

class InventorySystem {
  constructor() {
    /** @type {object[]} Shared party inventory bag. */
    this._bag = [];

    /** @type {import('../entities/Party.js').default|null} */
    this._party = null;
  }

  /**
   * Initialize with a party reference.
   * @param {import('../entities/Party.js').default} party
   */
  init(party) {
    this._party = party;
  }

  /**
   * Get the party reference.
   * @returns {import('../entities/Party.js').default|null}
   */
  getParty() {
    return this._party;
  }

  // ── Bag Operations ─────────────────────────────────

  /**
   * Add an item to the inventory bag.
   * @param {object} item
   * @returns {boolean} true if added, false if bag is full
   */
  addItem(item) {
    if (this._bag.length >= MAX_BAG_SIZE) return false;
    this._bag.push(item);
    eventBus.emit('inventory:add', { item });
    eventBus.emit('inventory:changed');
    return true;
  }

  /**
   * Remove an item from the bag by instance ID.
   * @param {string} instanceId
   * @returns {object|null} the removed item
   */
  removeItem(instanceId) {
    const idx = this._bag.findIndex(i => i.instanceId === instanceId);
    if (idx === -1) return null;
    const [item] = this._bag.splice(idx, 1);
    eventBus.emit('inventory:remove', { item });
    eventBus.emit('inventory:changed');
    return item;
  }

  /**
   * Get all items in the bag.
   * @returns {object[]}
   */
  getItems() {
    return [...this._bag];
  }

  /**
   * Get items in the bag that can go in a specific slot.
   * @param {string} slot
   * @returns {object[]}
   */
  getItemsBySlot(slot) {
    const normalizedSlot = slot.replace(/\d+$/, '');
    return this._bag.filter(item => {
      const itemSlot = item.slot.replace(/\d+$/, '');
      return itemSlot === normalizedSlot || item.slot === slot;
    });
  }

  /**
   * Get the current bag count.
   * @returns {number}
   */
  getBagCount() {
    return this._bag.length;
  }

  /**
   * Get the maximum bag size.
   * @returns {number}
   */
  getMaxBagSize() {
    return MAX_BAG_SIZE;
  }

  // ── Equipment Operations ───────────────────────────

  /**
   * Equip an item from the bag onto a character.
   * If the slot is occupied, the existing item is moved to the bag.
   *
   * @param {import('../entities/Character.js').default} character
   * @param {object} item - Item to equip (must be in the bag)
   * @param {string} [targetSlot] - Override slot (for ring1/ring2 choice)
   * @returns {boolean} true if equipped successfully
   */
  equipItem(character, item, targetSlot) {
    // Determine target slot
    let slot = targetSlot || item.slot;

    // Handle ring slot — if no targetSlot specified, pick the empty one or ring1
    if (item.slot === 'ring' && !targetSlot) {
      if (!character.equipment.ring1) slot = 'ring1';
      else if (!character.equipment.ring2) slot = 'ring2';
      else slot = 'ring1';
    }

    // Remove from bag
    const removed = this.removeItem(item.instanceId);
    if (!removed) return false;

    // Unequip current item in slot (if any)
    const previous = character.equipItem(slot, item);
    if (previous) {
      this.addItem(previous);
    }

    // Recalculate character stats
    this._recalculateStats(character);

    eventBus.emit('inventory:equip', { character, item, slot });
    eventBus.emit('inventory:changed');
    return true;
  }

  /**
   * Unequip an item from a character's slot and put it in the bag.
   *
   * @param {import('../entities/Character.js').default} character
   * @param {string} slot
   * @returns {boolean} true if unequipped successfully
   */
  unequipItem(character, slot) {
    const item = character.unequipItem(slot);
    if (!item) return false;

    if (!this.addItem(item)) {
      // Bag is full, re-equip
      character.equipItem(slot, item);
      return false;
    }

    this._recalculateStats(character);

    eventBus.emit('inventory:unequip', { character, item, slot });
    eventBus.emit('inventory:changed');
    return true;
  }

  /**
   * Get the equipment display info for a slot.
   * @param {string} slot
   * @returns {object}
   */
  static getSlotInfo(slot) {
    return SLOT_INFO[slot] || { name: slot, icon: '?' };
  }

  /**
   * Get all slot keys.
   * @returns {string[]}
   */
  static getSlotKeys() {
    return Object.keys(SLOT_INFO);
  }

  // ── Stat Recalculation ─────────────────────────────

  /**
   * Recalculate derived stats for a character based on equipment.
   * This is called automatically on equip/unequip.
   * @param {import('../entities/Character.js').default} character
   */
  _recalculateStats(character) {
    // Equipment stat bonuses are calculated live through getEquipmentStats()
    // We just need to update HP/mana pools
    const oldMaxHp = character.maxHp;
    const oldMaxMana = character.maxMana;

    // Character.maxHp and maxMana are getters that compute from attributes.
    // Equipment bonuses are applied via getEquipmentStats().
    // If HP/mana exceed max, clamp them.
    if (character.hp > character.maxHp) {
      character.hp = character.maxHp;
    }
    if (character.mana > character.maxMana) {
      character.mana = character.maxMana;
    }
  }

  /**
   * Get total equipment stat bonuses for a character.
   * Sums up all totalStats from equipped items.
   *
   * @param {import('../entities/Character.js').default} character
   * @returns {object} stat bonuses
   */
  getEquipmentStats(character) {
    const bonuses = {};

    for (const [slot, item] of Object.entries(character.equipment)) {
      if (!item) continue;
      const stats = item.totalStats || item.baseStats || {};
      for (const [stat, value] of Object.entries(stats)) {
        if (typeof value === 'number') {
          bonuses[stat] = (bonuses[stat] || 0) + value;
        }
      }
    }

    return bonuses;
  }

  /**
   * Get all affix effects from a character's equipment.
   * Useful for combat calculations.
   *
   * @param {import('../entities/Character.js').default} character
   * @returns {object[]} array of effect objects
   */
  getEquipmentEffects(character) {
    const effects = [];

    for (const [slot, item] of Object.entries(character.equipment)) {
      if (!item || !item.affixes) continue;
      for (const affix of item.affixes) {
        if (affix.effect) {
          effects.push({
            ...affix.effect,
            source: item.name,
            slot,
          });
        }
      }
    }

    return effects;
  }

  // ── Serialization ──────────────────────────────────

  /**
   * Serialize inventory state.
   * @returns {object}
   */
  toJSON() {
    return {
      bag: [...this._bag],
    };
  }

  /**
   * Restore inventory state from saved data.
   * @param {object} data
   */
  fromJSON(data) {
    this._bag = data.bag || [];
  }

  /**
   * Clear all inventory state.
   */
  clear() {
    this._bag = [];
    this._party = null;
  }
}

const inventorySystem = new InventorySystem();
export default inventorySystem;
export { SLOT_INFO, MAX_BAG_SIZE };
